import React, { useMemo } from 'react';
import { IngredientPurchaseRecord, TaxType } from '../../../../types';
import './TaxDistribution.scss';

interface TaxDistributionProps {
  purchases: IngredientPurchaseRecord[];
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(Math.round(value));

const TAX_CONFIG: Record<string, { label: string; colorClass: string }> = {
  deducible_iva: { label: 'Factura (con IVA)', colorClass: 'deducible' },
  no_deducible_iva: { label: 'Boleta (sin IVA)', colorClass: 'no-deducible' },
  no_recuperable: { label: 'No recuperable', colorClass: 'no-recuperable' },
  sin_clasificar: { label: 'Sin clasificar', colorClass: 'unclassified' },
};

const TaxDistribution: React.FC<TaxDistributionProps> = ({ purchases }) => {
  const taxData = useMemo(() => {
    const groups: Record<string, { total: number; taxAmount: number; count: number }> = {
      deducible_iva: { total: 0, taxAmount: 0, count: 0 },
      no_deducible_iva: { total: 0, taxAmount: 0, count: 0 },
      no_recuperable: { total: 0, taxAmount: 0, count: 0 },
      sin_clasificar: { total: 0, taxAmount: 0, count: 0 },
    };

    purchases.forEach(p => {
      const key = p.tax_type || 'sin_clasificar';
      groups[key].total += p.total_neto;
      groups[key].taxAmount += p.tax_amount || 0;
      groups[key].count += 1;
    });

    const grandTotal = Object.values(groups).reduce((sum, g) => sum + g.total, 0);

    return Object.entries(groups)
      .filter(([_, data]) => data.count > 0)
      .map(([key, data]) => ({
        key,
        ...TAX_CONFIG[key],
        ...data,
        percentage: grandTotal > 0 ? (data.total / grandTotal) * 100 : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [purchases]);

  if (taxData.length === 0) return null;

  const grandTotal = taxData.reduce((sum, item) => sum + item.total, 0);

  return (
    <div className="tax-distribution-card">
      <div className="card-header">
        <h3 className="card-title">Distribución de IVA</h3>
        <span className="card-subtitle">{purchases.length} compras</span>
      </div>
      <div className="card-body">
        {/* Stacked bar */}
        <div className="stacked-bar">
          {taxData.map(item => (
            <div
              key={item.key}
              className={`bar-segment ${item.colorClass}`}
              style={{ width: `${item.percentage}%` }}
              title={`${item.label}: ${item.percentage.toFixed(1)}%`}
            />
          ))}
        </div>

        {/* Legend with details */}
        <div className="tax-legend">
          {taxData.map(item => (
            <div key={item.key} className="legend-item">
              <div className="legend-left">
                <span className={`legend-dot ${item.colorClass}`} />
                <span className="legend-label">{item.label}</span>
                <span className="legend-count">{item.count}x</span>
              </div>
              <div className="legend-right">
                <span className="legend-amount">{formatCurrency(item.total)}</span>
                <span className="legend-percentage">{item.percentage.toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>

        {/* IVA summary */}
        {taxData.some(d => d.taxAmount > 0) && (
          <div className="iva-summary">
            <div className="iva-summary-row">
              <span>Total Neto</span>
              <span className="iva-summary-value">{formatCurrency(grandTotal)}</span>
            </div>
            <div className="iva-summary-row">
              <span>IVA Deducible</span>
              <span className="iva-summary-value">
                {formatCurrency(taxData.find(d => d.key === 'deducible_iva')?.taxAmount || 0)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxDistribution;
